import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SiteLayout from './layout/SiteLayout';
import Seo from './seo/Seo';
import { organizationJsonLd, websiteJsonLd } from '../lib/jsonld';
import './LandingPage.css';

const LANDING_IMAGES =
  'https://dhiifabjqmruggmpzgtb.supabase.co/storage/v1/object/public/public_images/landing_page';

function landingImageFolder(language) {
  return (language || '').toLowerCase().startsWith('en') ? 'us' : 'ua';
}

const IPHONE_FRAME = '/iphone-15-black.png';

function AppScreenshot({ file, alt, className, framed = false, localized = true }) {
  const { i18n } = useTranslation();
  const folder = localized ? landingImageFolder(i18n.language) : null;
  const relativePath = folder ? `${folder}/${file}` : file;
  const src = `${LANDING_IMAGES}/${folder ? `${folder}/` : ''}${encodeURIComponent(file)}`;
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setMissing(false);
  }, [src]);

  if (missing) {
    if (!framed) {
      return (
        <div className={`${className || ''} screenshot-slot`} role="img" aria-label={alt}>
          <span className="screenshot-slot-title">Upload</span>
          <span className="screenshot-slot-file">{relativePath}</span>
        </div>
      );
    }
    return (
      <div className={`iphone-mockup ${className || ''}`}>
        <div className="iphone-mockup-screen screenshot-slot" role="img" aria-label={alt}>
          <span className="screenshot-slot-title">Upload</span>
          <span className="screenshot-slot-file">{relativePath}</span>
        </div>
        <img className="iphone-mockup-device" src={IPHONE_FRAME} alt="" />
      </div>
    );
  }

  if (!framed) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        onError={() => setMissing(true)}
      />
    );
  }

  return (
    <div className={`iphone-mockup ${className || ''}`}>
      <div className="iphone-mockup-screen-wrap">
        <img
          className="iphone-mockup-screen"
          src={src}
          alt={alt}
          width={440}
          height={894}
          decoding="async"
          loading="lazy"
          onError={() => setMissing(true)}
        />
      </div>
      <span className="iphone-mockup-status" aria-hidden="true" />
      <img className="iphone-mockup-device" src={IPHONE_FRAME} alt="" />
    </div>
  );
}

function LandingPage() {
  const { t } = useTranslation();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <SiteLayout homeHeader onHomeNav={scrollToSection}>
      <Seo
        title="Baby-Led Weaning Meal Plans and Food Guides"
        description="Start solids with confidence. Tiny Spoon has BLW meal plans, a food encyclopedia, and expert guidance from 6 months — plus a public knowledge base for parents."
        path="/"
        type="website"
        jsonLd={[organizationJsonLd(), websiteJsonLd()]}
      />
      <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">{t('hero.title')}</h1>
            <p className="hero-subtitle">{t('hero.subtitle')}</p>
            <div className="hero-cta">
              <a 
                href={t('hero.cta.appStoreUrl')} 
                target="_blank" 
                rel="noopener noreferrer"
                className="download-btn download-btn-apple"
              >
                <img src="/apple.svg" alt="Apple" className="download-btn-icon" />
                <div className="download-btn-text">
                  <span className="download-btn-label">Download on the</span>
                  <span className="download-btn-name">App Store</span>
                </div>
              </a>
              <a
                href={t('hero.cta.playStoreUrl')}
                target="_blank"
                rel="noopener noreferrer"
                className="download-btn download-btn-google"
              >
                <img src="/google.svg" alt="Google Play" className="download-btn-icon" />
                <div className="download-btn-text">
                  <span className="download-btn-label">GET IT ON</span>
                  <span className="download-btn-name">Google Play</span>
                </div>
              </a>
            </div>
          </div>
          <div className="hero-visuals">
            <div className="hero-images">
              <AppScreenshot file="today.png" alt={t('hero.title')} className="hero-image" />
              <AppScreenshot file="blw guide detail.png" alt={t('features.guides.title')} className="hero-image" />
              <AppScreenshot file="food cut by age.png" alt={t('hero.title')} className="hero-image" />
            </div>
          </div>
        </div>
      </section>

      {/* Meal Plan Section */}
      <section id="mealPlan" className="meal-plan-section">
        <div className="container">
          <h2 className="section-title">{t('mealPlan.title')}</h2>
          <p className="meal-plan-subtitle">{t('mealPlan.subtitle')}</p>
          
          <div className="meal-plan-content">
            <div className="meal-plan-main">
              <div className="meal-plan-card">
                <div className="meal-plan-header">
                  <h3>{t('mealPlan.planTitle')}</h3>
                  <div className="meal-plan-badge">{t('mealPlan.badge')}</div>
                </div>
                <p className="meal-plan-description">{t('mealPlan.description')}</p>
                
                <div className="meal-plan-stats">
                  <div className="stat-item">
                    <div className="stat-number">30</div>
                    <div className="stat-label">{t('mealPlan.stats.days')}</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">30</div>
                    <div className="stat-label">{t('mealPlan.stats.meals')}</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">6</div>
                    <div className="stat-label">{t('mealPlan.stats.months')}</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">5</div>
                    <div className="stat-label">{t('mealPlan.stats.weeks')}</div>
                  </div>
                </div>

                <div className="meal-plan-features">
                  <h4>{t('mealPlan.features.title')}</h4>
                  <ul className="meal-plan-feature-list">
                    <li>{t('mealPlan.features.professional')}</li>
                    <li>{t('mealPlan.features.allergen')}</li>
                    <li>{t('mealPlan.features.variety')}</li>
                    <li>{t('mealPlan.features.guidelines')}</li>
                    <li>{t('mealPlan.features.progression')}</li>
                  </ul>
                </div>

                <div className="meal-plan-progression">
                  <h4>{t('mealPlan.progression.title')}</h4>
                  <div className="progression-timeline">
                    <div className="timeline-item">
                      <div className="timeline-week">{t('mealPlan.progression.week1.label')}</div>
                      <div className="timeline-content">{t('mealPlan.progression.week1.content')}</div>
                    </div>
                    <div className="timeline-item timeline-item-locked">
                      <div className="timeline-week">{t('mealPlan.progression.week2.label')}</div>
                      <div className="timeline-content">{t('mealPlan.progression.week2.content')}</div>
                      <div className="locked-overlay">
                        <span className="locked-text">{t('mealPlan.progression.locked')}</span>
                      </div>
                    </div>
                    <div className="timeline-item timeline-item-locked">
                      <div className="timeline-week">{t('mealPlan.progression.week3.label')}</div>
                      <div className="timeline-content">{t('mealPlan.progression.week3.content')}</div>
                      <div className="locked-overlay">
                        <span className="locked-text">{t('mealPlan.progression.locked')}</span>
                      </div>
                    </div>
                    <div className="timeline-item timeline-item-locked">
                      <div className="timeline-week">{t('mealPlan.progression.week4.label')}</div>
                      <div className="timeline-content">{t('mealPlan.progression.week4.content')}</div>
                      <div className="locked-overlay">
                        <span className="locked-text">{t('mealPlan.progression.locked')}</span>
                      </div>
                    </div>
                    <div className="timeline-item timeline-item-locked">
                      <div className="timeline-week">{t('mealPlan.progression.week5.label')}</div>
                      <div className="timeline-content">{t('mealPlan.progression.week5.content')}</div>
                      <div className="locked-overlay">
                        <span className="locked-text">{t('mealPlan.progression.locked')}</span>
                      </div>
                    </div>
                  </div>
                  <div className="meal-plan-cta">
                    <p className="meal-plan-cta-text">{t('mealPlan.cta.text')}</p>
                    <div className="meal-plan-cta-buttons">
                      <a 
                        href={t('cta.appStoreUrl')} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="download-btn download-btn-apple"
                      >
                        <img src="/apple.svg" alt="Apple" className="download-btn-icon" />
                        <div className="download-btn-text">
                          <span className="download-btn-label">Download on the</span>
                          <span className="download-btn-name">App Store</span>
                        </div>
                      </a>
                      <a
                        href={t('cta.playStoreUrl')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="download-btn download-btn-google"
                      >
                        <img src="/google.svg" alt="Google Play" className="download-btn-icon" />
                        <div className="download-btn-text">
                          <span className="download-btn-label">GET IT ON</span>
                          <span className="download-btn-name">Google Play</span>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="meal-plan-image">
              <AppScreenshot
                file="meals_plan_plates.svg"
                alt={t('mealPlan.title')}
                className="meal-plan-img"
                framed={false}
                localized={false}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="container">
          <h2 className="section-title">{t('features.title')}</h2>
          <div className="features-list">
            <div className="feature-item">
              <div className="feature-content">
                <h3>{t('features.today.title')}</h3>
                <p>{t('features.today.description')}</p>
                <ul className="feature-list">
                  <li>{t('features.today.items.today')}</li>
                  <li>{t('features.today.items.list')}</li>
                  <li>{t('features.today.items.preview')}</li>
                </ul>
              </div>
              <div className="feature-images">
                <AppScreenshot file="today.png" alt={t('features.today.items.today')} className="feature-img" />
                <AppScreenshot file="plans list.png" alt={t('features.today.items.list')} className="feature-img" />
                <AppScreenshot file="plan preview.png" alt={t('features.today.items.preview')} className="feature-img" />
              </div>
            </div>

            <div className="feature-item reverse">
              <div className="feature-content">
                <h3>{t('features.yearPlus.title')}</h3>
                <p>{t('features.yearPlus.description')}</p>
                <ul className="feature-list">
                  <li>{t('features.yearPlus.items.weekly')}</li>
                  <li>{t('features.yearPlus.items.generator')}</li>
                  <li>{t('features.yearPlus.items.grocery')}</li>
                </ul>
              </div>
              <div className="feature-images">
                <AppScreenshot file="weekly menu today.png" alt={t('features.yearPlus.items.weekly')} className="feature-img" />
                <AppScreenshot file="groccery list.png" alt={t('features.yearPlus.items.grocery')} className="feature-img" />
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-content">
                <h3>{t('features.meals.title')}</h3>
                <p>{t('features.meals.description')}</p>
                <ul className="feature-list">
                  <li>{t('features.meals.items.photos')}</li>
                  <li>{t('features.meals.items.filters')}</li>
                  <li>{t('features.meals.items.favorites')}</li>
                </ul>
              </div>
              <div className="feature-images">
                <AppScreenshot file="meals.png" alt={t('features.meals.title')} className="feature-img" />
                <AppScreenshot file="meal details.png" alt={t('features.meals.title')} className="feature-img" />
              </div>
            </div>

            <div className="feature-item reverse">
              <div className="feature-content">
                <h3>{t('features.foods.title')}</h3>
                <p>{t('features.foods.description')}</p>
                <ul className="feature-list">
                  <li>{t('features.foods.items.age')}</li>
                  <li>{t('features.foods.items.cutting')}</li>
                  <li>{t('features.foods.items.allergens')}</li>
                </ul>
              </div>
              <div className="feature-images">
                <AppScreenshot file="foods.png" alt={t('features.foods.title')} className="feature-img" />
                <AppScreenshot file="food cut by age.png" alt={t('features.foods.title')} className="feature-img" />
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-content">
                <h3>{t('features.tracker.title')}</h3>
                <p>{t('features.tracker.description')}</p>
                <ul className="feature-list">
                  <li>{t('features.tracker.items.introductionStatus')}</li>
                  <li>{t('features.tracker.items.historyLog')}</li>
                  <li>{t('features.tracker.items.savedFoods')}</li>
                </ul>
              </div>
              <div className="feature-images">
                <AppScreenshot file="track food.png" alt={t('features.tracker.title')} className="feature-img" />
                <AppScreenshot file="activity log.png" alt={t('features.tracker.items.historyLog')} className="feature-img" />
              </div>
            </div>

            <div id="guides" className="feature-item reverse">
              <div className="feature-content">
                <h3>{t('features.guides.title')}</h3>
                <p>{t('features.guides.description')}</p>
                <ul className="feature-list">
                  <li>{t('features.guides.items.safety')}</li>
                  <li>{t('features.guides.items.allergies')}</li>
                  <li>{t('features.guides.items.nutrition')}</li>
                </ul>
              </div>
              <div className="feature-images">
                <AppScreenshot file="guides list.png" alt={t('features.guides.title')} className="feature-img" />
                <AppScreenshot file="guide details.png" alt={t('features.guides.title')} className="feature-img" />
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-content">
                <h3>{t('features.aiAssistant.title')}</h3>
                <p>{t('features.aiAssistant.description')}</p>
                <ul className="feature-list">
                  <li>{t('features.aiAssistant.items.suggestions')}</li>
                  <li>{t('features.aiAssistant.items.replacements')}</li>
                  <li>{t('features.aiAssistant.items.safetyTips')}</li>
                </ul>
              </div>
              <div className="feature-images">
                <AppScreenshot file="ai chats list.png" alt={t('features.aiAssistant.title')} className="feature-img" />
                <AppScreenshot file="ai chat.png" alt={t('features.aiAssistant.title')} className="feature-img" />
                <AppScreenshot file="replace meal with ai.png" alt={t('features.aiAssistant.items.replacements')} className="feature-img" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Safety & Expertise Section */}
      <section id="safety" className="safety-section">
        <div className="container">
          <h2 className="section-title">{t('safety.title')}</h2>
          
          <div className="safety-content">
            <div className="safety-block">
              <h3>{t('safety.safetyFirst.title')}</h3>
              <p>{t('safety.safetyFirst.description')}</p>
              <ul className="safety-list">
                <li>{t('safety.safetyFirst.guidelines.who')}</li>
                <li>{t('safety.safetyFirst.guidelines.nhs')}</li>
                <li>{t('safety.safetyFirst.guidelines.canadian')}</li>
                <li>{t('safety.safetyFirst.guidelines.aap')}</li>
              </ul>
              <p className="safety-note">{t('safety.safetyFirst.note')}</p>
            </div>

            <div className="safety-block">
              <h3>{t('safety.expertValidation.title')}</h3>
              <p>{t('safety.expertValidation.description')}</p>
              <ul className="safety-list">
                <li>{t('safety.expertValidation.sources.canada')}</li>
                <li>{t('safety.expertValidation.sources.usa')}</li>
                <li>{t('safety.expertValidation.sources.eu')}</li>
              </ul>
              <p className="safety-note">{t('safety.expertValidation.inspiredBy')}</p>

              <div className="expert-cards-stack">
                <div className="expert-card-inline">
                  <div className="expert-image">
                    <img
                      src="/image_mon.jpeg"
                      alt={t('safety.expertValidation.canadianExpert.name')}
                      className="expert-photo"
                    />
                  </div>
                  <div className="expert-info">
                    <h4 className="expert-name">{t('safety.expertValidation.canadianExpert.name')}</h4>
                    <p className="expert-role">{t('safety.expertValidation.canadianExpert.role')}</p>
                    <p className="expert-location">{t('safety.expertValidation.canadianExpert.location')}</p>
                    <p className="expert-description">{t('safety.expertValidation.canadianExpert.description')}</p>
                  </div>
                </div>

                <div className="expert-card-inline">
                  <div className="expert-image">
                    <img
                      src="/image_vik.jpg"
                      alt={t('safety.expertValidation.ukraineExpert.name')}
                      className="expert-photo"
                    />
                  </div>
                  <div className="expert-info">
                    <h4 className="expert-name">{t('safety.expertValidation.ukraineExpert.name')}</h4>
                    <p className="expert-role">{t('safety.expertValidation.ukraineExpert.role')}</p>
                    <p className="expert-location">{t('safety.expertValidation.ukraineExpert.location')}</p>
                    <p className="expert-description">{t('safety.expertValidation.ukraineExpert.description')}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="safety-block disclaimer-block">
              <h3>{t('safety.disclaimer.title')}</h3>
              <p>{t('safety.disclaimer.text')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="download" className="cta-section">
        <div className="container">
          <h2 className="section-title">{t('cta.title')}</h2>
          <p className="cta-subtitle">{t('cta.subtitle')}</p>
          <div className="cta-buttons">
            <a 
              href={t('cta.appStoreUrl')} 
              target="_blank" 
              rel="noopener noreferrer"
              className="download-btn download-btn-apple"
            >
              <img src="/apple.svg" alt="Apple" className="download-btn-icon" />
              <div className="download-btn-text">
                <span className="download-btn-label">Download on the</span>
                <span className="download-btn-name">App Store</span>
              </div>
            </a>
            <a
              href={t('cta.playStoreUrl')}
              target="_blank"
              rel="noopener noreferrer"
              className="download-btn download-btn-google"
            >
              <img src="/google.svg" alt="Google Play" className="download-btn-icon" />
              <div className="download-btn-text">
                <span className="download-btn-label">GET IT ON</span>
                <span className="download-btn-name">Google Play</span>
              </div>
            </a>
          </div>
        </div>
      </section>
    </div>
    </SiteLayout>
  );
}

export default LandingPage;

